import { useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
import { parseApiError } from '../../lib/apiErrors';

const useChats = () => {
    const { getAccessTokenSilently, isAuthenticated } = useAuth0();
    const [posts, setPosts] = useState([]);
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(false);
    const selectedTopic = useSelector((state) => state.topic.selectedChatTopic);
    const apiUrl = process.env.REACT_APP_API_URL;

    const fetchPosts = useCallback(async (selectedTopicId) => {
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            const response = await fetch(`${apiUrl}/api/chats/${selectedTopicId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw await parseApiError(response, `Failed to fetch posts (${response.status})`);
            }

            const body = await response.text();
            const data = body ? JSON.parse(body) : [];
            const normalizedPosts = Array.isArray(data) ? data : data?.$values ?? [];
            setPosts(normalizedPosts.filter(post => !post.isDeleted));
        } catch (error) {
            console.error("Error fetching posts", error);
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently, apiUrl, isAuthenticated]);

    const fetchTopics = useCallback(async () => {
        if (!isAuthenticated) {
            setTopics([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            const response = await fetch(`${apiUrl}/api/chats`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw await parseApiError(response, `Failed to fetch topics (${response.status})`);
            }

            const body = await response.text();
            const data = body ? JSON.parse(body) : [];
            const normalizedTopics = Array.isArray(data) ? data : data?.$values ?? [];
            setTopics(normalizedTopics);
        } catch (error) {
            console.error("Error fetching topics", error);
        } finally {
            setLoading(false);
        }
    }, [getAccessTokenSilently, apiUrl, isAuthenticated]);

    const sendMessage = useCallback(async (topicId, message, atPostId = null, atUserId = null, attachment = null) => {
        if (!isAuthenticated) {
            throw new Error('User is not authenticated');
        }
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                },
            });

            let response;
            if (attachment) {
                const formData = new FormData();
                formData.append('TopicId', topicId);
                formData.append('Body', message);
                if (atPostId) {
                    formData.append('AtPostId', atPostId);
                }
                if (atUserId) {
                    formData.append('AtUserId', atUserId.replace(/[-|]/g, ''));
                }
                formData.append('Image', attachment);

                response = await fetch(`${apiUrl}/api/chats/postmessagewithimage`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                });
            } else {
                response = await fetch(`${apiUrl}/api/chats/postmessage`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        TopicId: topicId,
                        Body: message,
                        ...(atPostId ? { AtPostId: atPostId } : {}),
                        ...(atUserId ? { AtUserId: atUserId.replace(/[-|]/g, '') } : {}),
                    }),
                });
            }

            if (!response.ok) {
                throw await parseApiError(response, 'Failed to send message');
            }

            // Fetch all posts after sending a new message
            await fetchPosts(topicId);
        } catch (error) {
            console.error("Error sending message:", error);
            throw error;
        }
    }, [getAccessTokenSilently, apiUrl, fetchPosts, isAuthenticated]);

    return { posts, topics, loading, fetchPosts, fetchTopics, setPosts, sendMessage };
};

export default useChats;
